// @owners { team: patients-team }
import { ADD_OPTION_VALUE, ActionSheetContext, type AddOption, AltoIcon, InputSelect } from '@alto/design-system';
import React, { useCallback, useContext, useMemo } from 'react';
import { findMatchingPaymentMethod, formatPaymentMethod } from '~shared/features/payment-methods/helpers';
import { type PaymentMethod } from '~shared/types';
import { CreditCardActionSheet } from '~web/features/payments/components/CreditCardActionSheet';

const getPaymentMethodOptionProps = (paymentMethod: PaymentMethod) => ({
  label: formatPaymentMethod(paymentMethod),
  value: paymentMethod.id.toString(),
  LeftContent: <AltoIcon name="creditcard" />,
});

type Props = {
  readonly paymentMethodID: number | null | undefined;
  readonly paymentMethods: PaymentMethod[];
  readonly selectPaymentMethodID: (paymentMethodID: number) => void;
};

export const PaymentMethodDropdown = ({ paymentMethodID, paymentMethods, selectPaymentMethodID }: Props) => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);

  const handleSelectPaymentMethodID = useCallback(
    (selectedPaymentMethodID: string) => {
      selectPaymentMethodID(Number(selectedPaymentMethodID));
    },
    [selectPaymentMethodID],
  );

  const addPaymentMethodOptions: AddOption = useMemo(
    () => ({
      label: 'Add payment method',
      onPress: () => {
        setActiveActionSheet(<CreditCardActionSheet />);
      },
      value: ADD_OPTION_VALUE,
    }),
    [setActiveActionSheet],
  );

  const paymentMethodOptions = useCallback(() => {
    const options = paymentMethods.map(getPaymentMethodOptionProps);
    // @ts-expect-error add option is correct
    options.push(addPaymentMethodOptions);
    return options;
  }, [addPaymentMethodOptions, paymentMethods]);

  const isValidPaymentMethodID = !!findMatchingPaymentMethod(paymentMethods, paymentMethodID);

  if (!isValidPaymentMethodID) {
    handleSelectPaymentMethodID(String(paymentMethods[0].id));
  }

  return (
    <InputSelect
      label="Payment method"
      onValueChange={handleSelectPaymentMethodID}
      options={paymentMethodOptions()}
      value={paymentMethodID?.toString()}
      required
    />
  );
};
